/**
 * BrainFullEditor — Patch 37.4.3 · Brain Review Full Editing.
 *
 * Structured, single-page editor for the entire Brain question catalog.
 * Reads and writes the SAME canonical data as the guided Focus Flow —
 * every field maps to a ``BrainQuestion`` and every save goes through
 * ``useAnswerBrainQuestion``, which the backend routes through
 * ``BrainAnswerService.apply_answer`` → ``patch_section``. That means:
 *
 *   * the same HIC events fire for every edit;
 *   * the same audit history is written;
 *   * editing an approved brain automatically drops it back to draft
 *     (the backend's approval-reset behaviour); once the founder is
 *     done editing, the ``BrainMissionPage`` review banner asks them
 *     to review & re-approve.
 *
 * Founders NEVER see raw JSON. Every question is rendered with a
 * labeled input primitive appropriate to its ``kind``:
 *
 *   * ``short_text``  → text input
 *   * ``long_text``   → textarea
 *   * ``string_list`` → textarea, one item per line (round-trips as an
 *     array via ``linesToArray`` / ``arrayToLines`` — same convention
 *     Brand Brain has used since Patch 20)
 *   * ``choice``      → radio group (choices come from the catalog)
 *
 * Groups: Essentials (required) / Details that sharpen everything
 * (recommended) / Personalization (optional). Each group shows
 * answered + incomplete counts, and mobile viewports collapse the two
 * secondary groups behind the Radix Accordion.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import type {
  BrainMissionState,
  BrainMissionType,
  BrainQuestion,
} from "@/entities/brain-missions";
import { useAnswerBrainQuestion } from "@/entities/brain-missions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";

// ─── Group taxonomy (customer-facing labels — never "advanced") ────

interface GroupSpec {
  key: BrainQuestion["category"];
  label: string;
  blurb: string;
}

const GROUPS: GroupSpec[] = [
  {
    key: "required",
    label: "Essentials",
    blurb: "The answers Akinex needs before it can confidently represent you.",
  },
  {
    key: "recommended",
    label: "Details that sharpen everything",
    blurb: "Optional to start — but each one makes future campaigns feel more like you.",
  },
  {
    key: "optional",
    label: "Personalization",
    blurb: "The colour that makes you unmistakable. Nothing here is essential.",
  },
];

// ─── Serialisation between UI (always string) ↔ backend value ──────

function arrayToLines(v: unknown): string {
  if (Array.isArray(v)) return v.filter((s) => typeof s === "string").join("\n");
  if (typeof v === "string") return v;
  return "";
}

function linesToArray(v: string): string[] {
  return v
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** UI-shape of an answer: always a string, regardless of catalog kind. */
function hydrateField(q: BrainQuestion, current: unknown): string {
  if (q.kind === "string_list") return arrayToLines(current);
  if (q.kind === "choice") return typeof current === "string" ? current : "";
  return typeof current === "string" ? current : current == null ? "" : String(current);
}

function serialiseField(q: BrainQuestion, uiValue: string): unknown {
  if (q.kind === "string_list") return linesToArray(uiValue);
  return uiValue;
}

function isNonEmpty(q: BrainQuestion, uiValue: string): boolean {
  if (q.kind === "string_list") return linesToArray(uiValue).length > 0;
  return uiValue.trim().length > 0;
}

// ─── Props ─────────────────────────────────────────────────────────

interface BrainFullEditorProps {
  workspaceId: string;
  missionType: BrainMissionType;
  questions: BrainQuestion[];
  state: BrainMissionState;
  /** Current answers keyed by question id (server-canonical). */
  answers: Record<string, unknown>;
  /** Return to the guided review. */
  onBackToReview: () => void;
  /** Fires after ALL dirty answers persist successfully. Parent uses
   *  this to decide whether to switch to the review phase (which then
   *  surfaces the "Review & Approve" primary CTA). */
  onSaved: (summary: SaveSummary) => void;
}

export interface SaveSummary {
  changedCount: number;
  approvedBrainWasReset: boolean;
}

// ─── Editor ────────────────────────────────────────────────────────

export function BrainFullEditor({
  workspaceId,
  missionType,
  questions,
  state,
  answers,
  onBackToReview,
  onSaved,
}: BrainFullEditorProps): JSX.Element {
  const answerMut = useAnswerBrainQuestion(workspaceId, missionType);

  // Baseline (server-canonical, hydrated into UI strings). Frozen for
  // the life of this mount — the dirty check compares the live draft
  // to this. When the parent refetches after a save, it'll pass fresh
  // ``answers`` in and this effect resets the baseline + draft.
  const [baseline, setBaseline] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const q of questions) out[q.id] = hydrateField(q, answers[q.id]);
    return out;
  });
  const [draft, setDraft] = useState<Record<string, string>>(() => ({ ...baseline }));
  const [saving, setSaving] = useState(false);

  // Re-hydrate whenever the parent's answers change identity — e.g.
  // after a refetch, so the editor always reflects the server truth.
  useEffect(() => {
    const fresh: Record<string, string> = {};
    for (const q of questions) fresh[q.id] = hydrateField(q, answers[q.id]);
    setBaseline(fresh);
    setDraft(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, questions.length]);

  const grouped = useMemo(() => {
    const map = new Map<BrainQuestion["category"], BrainQuestion[]>();
    for (const q of questions) {
      if (!map.has(q.category)) map.set(q.category, []);
      map.get(q.category)!.push(q);
    }
    return map;
  }, [questions]);

  const dirtyIds = useMemo(() => {
    const ids: string[] = [];
    for (const q of questions) {
      if ((draft[q.id] ?? "") !== (baseline[q.id] ?? "")) ids.push(q.id);
    }
    return ids;
  }, [draft, baseline, questions]);

  const isDirty = dirtyIds.length > 0;

  const setField = (qid: string, value: string) => {
    setDraft((d) => ({ ...d, [qid]: value }));
  };

  const brainWasApproved = state.brain_status === "approved";

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    let succeeded = 0;
    try {
      // Persist changed answers sequentially so the last-writer-wins
      // order is deterministic and matches Focus Flow's per-answer
      // apply semantics. (The catalog is ~30 questions max; in
      // practice the founder is editing a handful.)
      for (const qid of dirtyIds) {
        const q = questions.find((x) => x.id === qid);
        if (!q) continue;
        const raw = draft[qid] ?? "";
        // Empty non-required field — treat as "cleared" but still send
        // to preserve the founder's intent. Required fields must not
        // be cleared through this editor; the UI won't fire the save
        // for a cleared required field (the input still holds the
        // empty string, and the save iterates dirtyIds which does
        // include it — so we guard here).
        if (q.category === "required" && !isNonEmpty(q, raw)) {
          toast.error(`"${q.label}" is required. Add an answer before saving.`);
          continue;
        }
        await answerMut.mutateAsync({
          question_id: qid,
          value: serialiseField(q, raw),
        });
        succeeded += 1;
      }
      if (succeeded > 0) {
        toast.success(
          succeeded === 1
            ? "1 answer saved."
            : `${succeeded} answers saved.`,
        );
      }
      onSaved({
        changedCount: succeeded,
        approvedBrainWasReset: brainWasApproved && succeeded > 0,
      });
    } catch {
      toast.error("We couldn't save every answer. The ones that failed are still highlighted.");
    } finally {
      setSaving(false);
    }
  };

  // Counts per group.
  const groupCounts = (group: BrainQuestion["category"]) => {
    const list = grouped.get(group) ?? [];
    let answered = 0;
    let incomplete = 0;
    for (const q of list) {
      if (isNonEmpty(q, draft[q.id] ?? "")) answered += 1;
      else incomplete += 1;
    }
    return { answered, incomplete, total: list.length };
  };

  return (
    <div
      className="mx-auto flex max-w-3xl flex-col gap-6 text-white"
      data-testid="brain-full-editor"
      data-mission-type={missionType}
      data-dirty={isDirty}
    >
      <header className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold leading-snug tracking-tight sm:text-3xl">
          Edit all answers
        </h2>
        <p className="text-sm leading-relaxed text-white/70">
          Every answer you&apos;ve taught Akinex, in one place. Update as many as
          you like — nothing is committed until you tap Save Changes.
        </p>
        {brainWasApproved ? (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100/90"
            data-testid="brain-full-editor-approved-warning"
          >
            Editing an approved brain drops it back into review. Once your
            changes are saved, review and approve them before Akinex uses
            the updated brain.
          </div>
        ) : null}
      </header>

      {/* Desktop: all three groups stacked. Mobile: Essentials open,
          the other two behind collapsible accordions. Radix Accordion
          respects the ``defaultValue`` so the initial state is
          deterministic. */}
      <div className="hidden flex-col gap-8 sm:flex" data-testid="brain-full-editor-desktop">
        {GROUPS.map((g) => (
          <GroupSection
            key={g.key}
            spec={g}
            questions={grouped.get(g.key) ?? []}
            draft={draft}
            baseline={baseline}
            onFieldChange={setField}
            counts={groupCounts(g.key)}
            skippedIds={state.skipped_ids}
          />
        ))}
      </div>
      <Accordion
        type="multiple"
        defaultValue={["required"]}
        className="flex flex-col gap-3 sm:hidden"
        data-testid="brain-full-editor-mobile"
      >
        {GROUPS.map((g) => {
          const counts = groupCounts(g.key);
          return (
            <AccordionItem
              key={g.key}
              value={g.key}
              className="rounded-2xl border border-white/10 bg-white/[0.02] px-4"
              data-testid={`brain-full-editor-mobile-group-${g.key}`}
            >
              <AccordionTrigger className="py-3 text-left hover:no-underline">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">
                    {g.label}
                  </div>
                  <div className="mt-0.5 text-[11px] text-white/50">
                    {counts.answered} answered · {counts.incomplete} incomplete
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pt-1">
                <p className="mb-3 text-xs text-white/50">{g.blurb}</p>
                <div className="flex flex-col gap-5">
                  {(grouped.get(g.key) ?? []).map((q) => (
                    <FieldRow
                      key={q.id}
                      q={q}
                      value={draft[q.id] ?? ""}
                      baseline={baseline[q.id] ?? ""}
                      onChange={(v) => setField(q.id, v)}
                      skipped={state.skipped_ids.includes(q.id)}
                    />
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <footer
        className="sticky bottom-0 z-10 -mx-4 flex flex-col-reverse gap-3 border-t border-white/10 bg-slate-950/85 px-4 py-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-between sm:rounded-2xl sm:border sm:bg-white/[0.03]"
        data-testid="brain-full-editor-footer"
      >
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onBackToReview}
            data-testid="brain-full-editor-back-to-review"
            className="text-xs text-white/70 hover:text-white"
          >
            ← Back to Review
          </Button>
          <span className="text-[11px] text-white/50" data-testid="brain-full-editor-dirty-count">
            {isDirty
              ? `${dirtyIds.length} unsaved ${dirtyIds.length === 1 ? "change" : "changes"}`
              : "No unsaved changes"}
          </span>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          aria-busy={saving}
          data-testid="brain-full-editor-save"
          className="min-h-[44px] px-6 text-sm font-semibold"
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </footer>
    </div>
  );
}

// ─── Group section (desktop) ───────────────────────────────────────

function GroupSection({
  spec,
  questions,
  draft,
  baseline,
  onFieldChange,
  counts,
  skippedIds,
}: {
  spec: GroupSpec;
  questions: BrainQuestion[];
  draft: Record<string, string>;
  baseline: Record<string, string>;
  onFieldChange: (qid: string, value: string) => void;
  counts: { answered: number; incomplete: number; total: number };
  skippedIds: string[];
}): JSX.Element | null {
  if (questions.length === 0) return null;
  return (
    <section
      className="flex flex-col gap-4"
      data-testid={`brain-full-editor-group-${spec.key}`}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80">
            {spec.label}
          </div>
          <p className="mt-1 text-xs text-white/50">{spec.blurb}</p>
        </div>
        <div
          className="shrink-0 text-[11px] text-white/50"
          data-testid={`brain-full-editor-group-${spec.key}-counts`}
        >
          {counts.answered} answered · {counts.incomplete} incomplete
        </div>
      </div>
      <div className="flex flex-col gap-5">
        {questions.map((q) => (
          <FieldRow
            key={q.id}
            q={q}
            value={draft[q.id] ?? ""}
            baseline={baseline[q.id] ?? ""}
            onChange={(v) => onFieldChange(q.id, v)}
            skipped={skippedIds.includes(q.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ─── Single field ─────────────────────────────────────────────────

function FieldRow({
  q,
  value,
  baseline,
  onChange,
  skipped,
}: {
  q: BrainQuestion;
  value: string;
  baseline: string;
  onChange: (v: string) => void;
  skipped: boolean;
}): JSX.Element {
  const inputId = `bfe-${q.id}`;
  const changed = value !== baseline;
  const empty = !isNonEmpty(q, value);
  const showSkipHint = skipped && baseline === "";

  const commonClasses =
    "mt-1.5 rounded-md border border-white/15 bg-white/[0.04] text-sm text-white placeholder:text-white/30 focus-visible:border-cyan-400 focus-visible:ring-1 focus-visible:ring-cyan-400";

  return (
    <div
      className="flex flex-col gap-1"
      data-testid={`brain-full-editor-field-${q.id}`}
      data-changed={changed}
      data-empty={empty}
    >
      <Label htmlFor={inputId} className="text-xs font-medium text-white/85">
        {q.label}
        {q.category === "required" ? (
          <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-300/80">
            required
          </span>
        ) : null}
      </Label>
      {q.why_it_matters ? (
        <p className="text-[11px] leading-relaxed text-white/50">{q.why_it_matters}</p>
      ) : null}

      {q.kind === "choice" ? (
        <div
          role="radiogroup"
          aria-labelledby={inputId}
          className="mt-1.5 flex flex-wrap gap-2"
        >
          {q.choices.map((choice) => {
            const active = value === choice;
            return (
              <button
                key={choice}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onChange(choice)}
                data-testid={`brain-full-editor-choice-${q.id}-${choice}`}
                className={
                  "rounded-full border px-3 py-1.5 text-xs transition "
                  + (active
                    ? "border-cyan-400 bg-cyan-400/10 text-cyan-100"
                    : "border-white/15 text-white/70 hover:border-white/30")
                }
              >
                {choice}
              </button>
            );
          })}
        </div>
      ) : q.kind === "long_text" || q.kind === "string_list" ? (
        <Textarea
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={q.kind === "string_list" ? 4 : 3}
          className={commonClasses}
          placeholder={
            q.example_answers?.[0]
              ?? (q.kind === "string_list" ? "One per line…" : "Type your answer…")
          }
          data-testid={`brain-full-editor-input-${q.id}`}
        />
      ) : (
        <Input
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={commonClasses}
          placeholder={q.example_answers?.[0] ?? "Type your answer…"}
          data-testid={`brain-full-editor-input-${q.id}`}
        />
      )}

      {q.kind === "string_list" ? (
        <p className="text-[10px] text-white/40">Add each item on its own line.</p>
      ) : null}
      {showSkipHint ? (
        <p className="text-[10px] text-white/40">You skipped this earlier — filling it in now unskips it.</p>
      ) : null}
    </div>
  );
}
